/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { cloneDeep, isNil, map, negate, pickBy } from 'lodash';
import arrayMutators from 'final-form-arrays';
import { useContext, useEffect, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { css, StyleSheet } from 'aphrodite';
import { ActionButton, BrandColors, Text } from '@blend/enterprise-components';

import { getTitleCuratives, getTitleOrderByLoanId, putTitleCuratives } from 'blb-api/title';
import { SubjectContext } from 'blb-contexts/Subject';

import { CurativeTable } from './CurativeTable';
import { CurativeConfig, TitleContext } from './TitleTab';
import { FormValues } from './types';
import { MutableState } from 'final-form';
import { legalDescriptionInputConfig, policyAmountInputConfig, vestingInputConfig } from './inputConfigs';
import { CommitmentErrorMessage } from './constants';

interface CommitmentCardProps {
  curativeConfig: CurativeConfig;
}

const isNotNil = negate(isNil);

const styles = StyleSheet.create({
  column2: {
    width: '100%',
  },
  columns: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  curativeCard: {
    backgroundColor: 'white',
    border: `1px solid ${BrandColors.CLOUD_30}`,
    borderRadius: 3,
    padding: 20,
  },
  curativeFooter: {
    borderTop: `1px solid ${BrandColors.CLOUD_30}`,
    paddingBottom: 15,
    paddingRight: 30,
    paddingTop: 15,
    textAlign: 'right',
    width: '100%',
  },
  curativeHeader: {
    backgroundColor: BrandColors.CLOUD_30,
    marginBottom: 10,
    padding: '8px 16px',
  },
  curativeSection: {
    marginBottom: 30,
    paddingLeft: 10,
    paddingRight: 10,
  },
});

export const CommitmentCard: React.FC<CommitmentCardProps> = ({ curativeConfig }) => {
  const [initLiens, setLiens] = useState<Partial<Title.Orders.Lien>[]>([]);
  const [initTaxes, setTaxes] = useState<Partial<Title.Orders.Tax>[]>([]);
  const [initPolicyAmount, setPolicyAmount] = useState<number | undefined>(undefined);
  const [initRequirements, setRequirements] = useState<Partial<Title.CommitmentRequirement>[]>([]);
  const [initVesting, setVesting] = useState<string | undefined>('');
  const [initLegalDescription, setLegalDescription] = useState<string | undefined>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formRefresh, setFormRefresh] = useState<number>(0);
  const { commitmentHeaderRef, order, setCommitmentError, setTitleOrder, loanType } = useContext(TitleContext);
  const subjectContext = useContext(SubjectContext);
  const [isAutomated, setIsAutomated] = useState<boolean>(false);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      const isTitleCurativeEnabled = subjectContext?.agency?.generalTitleConfiguration?.titleCurative;
      if (order && isTitleCurativeEnabled) {
        const {
          data: { liens, taxes, policyAmount, requirements, vesting, legalDescription },
        } = await getTitleCuratives(order.id, order.tenant);
        taxes.map(tax => {
          tax.dueDate = new Date(tax.dueDate).getTime();
        });
        setLiens(liens);
        setTaxes(taxes);
        setPolicyAmount(policyAmount);
        setRequirements(requirements);
        setVesting(vesting);
        setLegalDescription(legalDescription);
        // NOTE: (TITLE-1781) not currently disabling any fields for editing, so we always set this to `false`
        setIsAutomated(false);
      }
    })();
  }, [formRefresh]);

  const scrollToCommitmentHeader = () => {
    commitmentHeaderRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSave = async (values: FormValues) => {
    const status = curativeConfig.curativeStatus;
    if (!order?.commitmentDocID) {
      scrollToCommitmentHeader();
      setCommitmentError(curativeConfig.documentErrorText);
      return;
    }
    setSubmitting(true);
    const { liens, taxes, requirements } = values;
    try {
      const liensToUpdate = map(liens, (lien, index) => {
        lien.position = index;
        lien.status = (status || lien.status) as Title.Orders.LienStatusType;
        return lien;
      });
      const taxesToUpdate = cloneDeep(taxes);
      for (const tax of taxesToUpdate) {
        tax.dueDate = new Date(tax.dueDate!).toISOString();
      }
      const requirementsToUpdate = map(requirements, requirement => {
        requirement.status = (status || requirement.status) as Title.CommitmentRequirementStatus;
        return requirement;
      });
      await putTitleCuratives(order.id, order.tenant, {
        // omit null/undefined values
        ...pickBy(values, isNotNil),
        liens: liensToUpdate,
        taxes: taxesToUpdate,
        requirements: requirementsToUpdate,
      });
      const { data: updatedOrder } = await getTitleOrderByLoanId(order.applicationID);
      setTitleOrder(updatedOrder);
      setCommitmentError('');
    } catch (err) {
      scrollToCommitmentHeader();
      setCommitmentError(CommitmentErrorMessage.SAVE_FAILURE);
    } finally {
      setSubmitting(false);
      // refresh curatives every time form is submitted
      setFormRefresh(formRefresh + 1);
    }
  };

  const updateDocId = (args: any[], state: MutableState<FormValues>) => {
    const [rowName, docId] = args;
    const field = state.fields[`${rowName}.docId`];
    field.change(docId);
  };

  const renderVestingAndPolicyAmountSection = (loanType?: string) => {
    const hidePolicyAmount = loanType && ['HELOAN', 'HELOC'].includes(loanType);

    const vestingSection = (
      <section className={css(styles.curativeSection, styles.column2)}>
        <div className={css(styles.curativeHeader)}>
          <Text weight="semibold">Title Vesting</Text>
        </div>
        <Field disabled={isAutomated} {...vestingInputConfig} />
      </section>
    );
    const policyAmountSection = (
      <section className={css(styles.curativeSection, styles.column2)}>
        <div className={css(styles.curativeHeader)}>
          <Text weight="semibold">Title Policy Amount</Text>
        </div>
        <Field disabled={isAutomated} {...policyAmountInputConfig} />
      </section>
    );

    return (
      <div className={css(styles.columns)}>
        {vestingSection}
        {!hidePolicyAmount && policyAmountSection}
      </div>
    );
  };

  return (
    <div className={css(styles.curativeCard)}>
      <Form
        initialValues={{
          legalDescription: initLegalDescription,
          liens: initLiens,
          policyAmount: initPolicyAmount,
          requirements: initRequirements,
          taxes: initTaxes,
          vesting: initVesting,
        }}
        mutators={{
          updateDocId,
          ...arrayMutators,
        }}
        onSubmit={handleSave}
        render={({ form, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            {renderVestingAndPolicyAmountSection(loanType)}
            <section className={css(styles.curativeSection)}>
              <div className={css(styles.curativeHeader)}>
                <Text weight="semibold">Legal Description</Text>
              </div>
              <Field disabled={isAutomated} {...legalDescriptionInputConfig} />
            </section>
            <section className={css(styles.curativeSection)}>
              <div className={css(styles.curativeHeader)}>
                <Text weight="semibold">Liens & Judgments</Text>
              </div>
              <CurativeTable
                columnWidths={[1, 1, 1, 1, 1]}
                form={form}
                headerStyle={{ color: BrandColors.NIGHT_90, fontFamily: 'Roboto, sans-serif', fontSize: '12px' }}
                headerTexts={curativeConfig.lienColumnNames}
                isDisabled={isAutomated}
                name="liens"
                rightAlignLastCol={false}
              />
            </section>
            <section className={css(styles.curativeSection)}>
              <div className={css(styles.curativeHeader)}>
                <Text weight="semibold">Taxes</Text>
              </div>
              <CurativeTable
                form={form}
                headerStyle={{ color: BrandColors.NIGHT_90, fontFamily: 'Roboto, sans-serif', fontSize: '12px' }}
                headerTexts={['Tax Name', 'Due Date', 'Total Taxes', 'Status', 'Action']}
                isDisabled={isAutomated}
                name="taxes"
                rightAlignLastCol={false}
              />
            </section>
            <section className={css(styles.curativeSection)}>
              <div className={css(styles.curativeHeader)}>
                <Text weight="semibold">Additional Requirements</Text>
              </div>
              <CurativeTable
                columnWidths={curativeConfig.requirementColumnWidths}
                form={form}
                headerStyle={{ color: BrandColors.NIGHT_90, fontFamily: 'Roboto, sans-serif', fontSize: '12px' }}
                headerTexts={curativeConfig.requirementColumnNames}
                isDisabled={isAutomated}
                name="requirements"
                rightAlignLastCol={false}
              />
            </section>
            <footer className={css(styles.curativeFooter)}>
              <ActionButton
                disabled={submitting}
                id="CommitmentCard-Save-curatives"
                isLoading={submitting}
                label="Save & Display in Blend"
                onClick={handleSubmit}
                theme={submitting ? 'tertiary' : 'primary'}
              />
            </footer>
          </form>
        )}
      />
    </div>
  );
};
